/* eslint-disable camelcase */
/* eslint-disable no-underscore-dangle */
import { createSelector } from '@reduxjs/toolkit';
import { get, isArray } from 'lodash';
import { getFromImmutableState } from '../../../../utils/immutableUtils';

import { getRouletteHotelTitle } from '../../../../utils/stringUtils';

/**
 * Возвращает иммутабельный отель:
 * @param {object} state reducer
 * @returns immutable hotel
 */
const getHotel = (state) => state.get('hotel');

/**
 * Возвращает id отеля
 * @param {object} state reducer
 * @returns hotel id(number|stirng)
 */
export const getHotelId = (state) => getHotel(state).get('id');

/**
 * Возвращает имя отеля
 * @param {object} state reducer
 * @returns hotel name(stirng)
 */
export const getHotelName = (state) => getHotel(state).get('name');

/**
 * Возвращает ссылку на отель
 * @param {object} state reducer
 * @returns hotel public_url(stirng)
 */
export const getHotelLink = (state) => getHotel(state).get('public_url');

/**
 * @param {object} state reducer
 * @returns наличие мест в отеле
 */
export const getHotelAccomodationAvailability = (state) =>
  getHotel(state).get('accommodation_availability');

/**
 * Возвращает количество звезд отеля
 * @param {object} state reducer
 * @returns количество звезд отеля
 */
export const getHotelStars = (state) => getHotel(state).get('stars');

/**
 * Возвращает id местоположения отеля
 * @param {object} state reducer
 * @returns id местоположения отеля
 */
export const getHotelPlaceId = (state) => getHotel(state).get('place_id');

/**
 * Возвращает местоположение отеля
 * @param {object} state reducer
 * @returns местоположение отеля
 */
export const getHotelPlace = (state) =>
  getHotel(state).getIn(['place', 'name_ru']);

/**
 * Возвращает название страны, в котором находится отель
 * @param {object} state reducer
 * @returns название страны, в котором находится отель
 */
export const getHotelCountryName = (state) =>
  getHotel(state).getIn(['country', 'name_ru']);

/**
 * Возвращает цену отеля
 * @param {object} state reducer
 * @returns цену отеля
 */
export const getInitialPrice = (state) => getHotel(state).get('price');

/**
 * Возвращает расположение отеля
 * @param {function} getHotelCountryName selector
 * @param {function} getHotelPlace selector
 * @returns расположение отеля
 */
export const getHotelLocation = createSelector(
  [getHotelCountryName, getHotelPlace],
  (country, place) => `${place}, ${country}`,
);

/**
 * Возвращает минимальную цену из всех предложений в отеле
 * @param {object} state reducer
 * @returns минимальную цену из всех предложений в отеле
 */
export const getHotelMinPrice = createSelector([], (allPrices) =>
  isArray(allPrices) ? Math.min(...allPrices) : allPrices,
);

/**
 * Возвращает информацию об отеле
 * @param {object} state reducer
 * @returns информацию об отеле
 */
export const getHotelInfo = (state) => getHotel(state).get('hotel_info');

/**
 * Возвращает короткую информацию об отеле
 * @param {object} state reducer
 * @returns короткую информацию об отеле
 */
export const getHotelShortInfo = (state) => getHotel(state).get('short_info');

/**
 * Возвращает широту координатов отеля
 * @param {object} state reducer
 * @returns широту координатов отеля
 */
const getHotelLat = (state) => getHotel(state).get('lat');

/**
 * Возвращает долготу координатов отеля
 * @param {object} state reducer
 * @returns долготу координатов отеля
 */
const getHotelLong = (state) => getHotel(state).get('long');

/**
 * Возвращает координаты отеля
 * @param {function} getHotelLat selector
 * @param {function} getHotelLong selector
 * @returns координаты отеля
 */
export const getHotelCoordinates = createSelector(
  [getHotelLat, getHotelLong],
  (lat, long) => {
    if (!Number(lat) && !Number(long)) {
      return null;
    }

    return {
      lat,
      long,
    };
  },
);

/**
 * Возвращает главную картинку
 * @param {object} state reducer
 * @returns главную картинку
 */
const getHotelMainImage = getFromImmutableState((state) =>
  getHotel(state).get('main_image'),
);

/**
 * Возвращает ссылку на главную картинку
 * @param {object} state reducer
 * @returns ссылку на главную картинку
 */
export const getHotelMainImageHref = createSelector(
  getHotelMainImage,
  (image) => get(image, ['file', 'x100x115', 'url']),
);

/**
 * Возвращает рулетку от отеля Fortuna*
 * @param {object} state reducer
 * @returns рулетку от отеля Fortuna*
 */
export const getHotelRoulette = (state) => getHotel(state).get('roulette');

/**
 * Возвращает заголовок в шапке отеля
 * @param {object} state reducer
 * @returns заголовок в шапке отеля
 */
export const getHotelTitle = createSelector(
  [getHotelName, getHotelStars, getHotelRoulette],
  (name, stars, isRoutlette) =>
    isRoutlette ? getRouletteHotelTitle(name, stars) : name,
);
